import PageFilter from '../../components/PageFilter';
import SyncSegmentsCards from '../../components/SyncSegments/SyncSegmentsCards';

import { Flex, Grid } from '@mantine/core';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import NewElementButton from '../../components/NewElementButton';
import Pager from '../../components/Pager/Pager';
import SearchBar from '../../components/SearchBar';
import Spinner from '../../components/Spinner';
import PageSizeOptions from '../../constants/pageSizeOptions';
import { Page } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';

const fetchDebounced = AwesomeDebouncePromise((fetch, accountId, domainId, filter) => {
  return fetch(
    `/api/admin/accounts/${accountId}/domains/${domainId}/segmentSyncs?${queryString.stringify({ ...filter })}`
  );
}, 300);

const SyncSegmentsPage = () => {
  const fetch = useFetch();
  const location = useLocation();
  const navigate = useNavigate();
  const { accountId, domainId } = useParams();

  const selectedPageSize = parseInt(queryString.parse(location.search)?.resultsPerPage) || PageSizeOptions[0];
  const selectedPage = parseInt(queryString.parse(location.search)?.page) || 1;
  const searchValue = queryString.parse(location.search)?.searchTerms || '';

  const [segmentSyncs, setSegmentSyncs] = useState();
  const [error, setError] = useState();
  const [pageCount, setPageCount] = useState();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);

    const filterDefaultValues = { page: 1, resultsPerPage: selectedPageSize, searchTerms: '' };
    const filter = { ...filterDefaultValues, ...queryString.parse(location.search) };

    fetchDebounced(fetch, accountId, domainId, filter)
      .then(({ segmentSyncs, pagesCount }) => {
        setSegmentSyncs(segmentSyncs);
        setPageCount(pagesCount);
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [domainId, accountId, fetch, location.search, selectedPageSize]);

  const setPageSize = async (pageSize) => {
    navigate({
      search: queryString.stringify({ ...queryString.parse(location.search), page: 1, resultsPerPage: pageSize }),
    });
  };

  const toggleSegmentSync = (segmentSyncIndex, running) => {
    const segmentSync = segmentSyncs[segmentSyncIndex];
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segmentSyncs/${segmentSync.id}`, {
      method: 'PUT',
      body: JSON.stringify({ running }),
    })
      .then(({ segmentSync }) => {
        const updatedSegmentSyncs = [...segmentSyncs];
        updatedSegmentSyncs[segmentSyncIndex] = segmentSync;
        setSegmentSyncs(updatedSegmentSyncs);
      })
      .catch((e) => setError(e));
  };

  const onSearchChange = (e) => {
    const newFilter = { ...queryString.parse(location.search), searchTerms: e.target.value.toLowerCase(), page: 1 };
    navigate({ search: queryString.stringify({ ...newFilter }) });
  };

  const handlePageClick = (e) => {
    const currentPage = e.selected;
    navigate({ search: queryString.stringify({ ...queryString.parse(location.search), page: currentPage }) });
  };

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (!segmentSyncs) {
    return <Spinner />;
  }

  const segmentSyncElements = (
    <SyncSegmentsCards segmentSyncs={segmentSyncs} toggleSegmentSync={toggleSegmentSync} url={location.pathname} />
  );

  const search = queryString.parse(location.search);

  return (
    <div data-tour="syncSegmentsOverview">
      <Metadata title={`${Page['syncSegments'] || 'Sync Segments'}`} />
      <Grid mb="md">
        <Grid.Col span={6}>
          <Flex align="center" gap="md">
            <PageFilter
              placeholder="Type"
              queryKey="type"
              options={[
                { value: 'facebook', label: 'Facebook' },
                { value: 'google', label: 'Google' },
              ]}
            />

            <PageFilter
              placeholder="On / Off"
              queryKey="running"
              options={[
                { value: 'true', label: 'On' },
                { value: 'false', label: 'Off' },
              ]}
            />
          </Flex>
        </Grid.Col>
        <Grid.Col span={6} ta="right">
          <NewElementButton
            onClick={() =>
              navigate({
                ...location,
                pathname: location.pathname + '/new',
                search: queryString.stringify({
                  ...search,
                }),
              })
            }
            title="New Segment Sync"
            restrictionKey="syncSegmentsCreate"
          />
        </Grid.Col>
      </Grid>

      <SearchBar value={searchValue} onChange={onSearchChange} />

      {loading ? (
        <Spinner />
      ) : (
        <>
          {segmentSyncElements}

          <Pager
            pageSize={selectedPageSize}
            pageCount={pageCount}
            onPageChange={handlePageClick}
            initialPage={selectedPage}
            setPageSize={setPageSize}
          />
        </>
      )}
    </div>
  );
};

export default SyncSegmentsPage;
